// apps/client/src/pages/leads/LeadPipelinePage.tsx
//
// Unified Pipeline page (PR-3 chunk 4).
// Bird's-eye view of every Valutten lead + every CRM deal, grouped under
// the cross-CRM unified stage taxonomy from
// `packages/shared-schemas/src/crmStageMapping.ts`. Admin-only.
//
// Data sources:
//   - Firestore `companies/{id}/leads` (onSnapshot, limit 500) — live
//     lead rows for the lead-side cards.
//   - Callable `getUnifiedPipeline` — latest-snapshot CRM deals grouped
//     into the same stage taxonomy on the BQ side.
//
// The two streams are merged client-side. A lead and a deal that
// represent the same logical client (matched by linked_loan_id or by
// lowercased email) collapse to a single card under the more-progressed
// stage so we don't double-count.

import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Layers,
  Users,
  Briefcase,
  TrendingUp,
  PiggyBank,
  Filter,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { db, functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
// Sub-path import — bypasses the shared-schemas barrel (which loads
// crypto-using modules brokerIdentity + loanFingerprinting). Webpack 5
// in CRA doesn't polyfill crypto. Same pattern as
// @valutten/shared-utils/auth.
import {
  toUnifiedStage,
  UNIFIED_STAGES,
  type UnifiedStage,
} from '@valutten/shared-schemas/dist/crmStageMapping';
import { formatCompactCurrency } from '../../utils/formatters';

type OutletCtx = {
  userData?: { uid?: string; companyId?: string; role?: string; officeIds?: string[] };
};

type LookbackDays = 7 | 30 | 90 | 0;

// VALUTTEN Growth derived deal stage: set by getUnifiedPipeline only for
// companies at featureFlags.growth = 'propose'; absent for everyone else.
type DerivedStage = 'settled' | 'approved' | 'application';

const DERIVED_STAGE_LABEL: Record<DerivedStage, string> = {
  settled: 'Settled',
  approved: 'Approved',
  application: 'Application',
};

interface UnifiedCard {
  kind: 'lead' | 'deal';
  id: string;
  clientName: string;
  source: string; // 'website_form' | 'meta_ads' | … | 'mycrm' | 'nodify'
  valueCents: number | null;
  ageDays: number;
  assignedBrokerName: string | null;
  linkedLoanId: string | null;
  rawStatus: string | null;
  unifiedStage: UnifiedStage;
  capturedAt: string | null;
  serviceCategory: string | null;
  // Bucket keys used for deal⇆lead collapse.
  emailLower: string | null;
  derivedStage?: DerivedStage | null;
}

// Server response shape (mirrors getUnifiedPipeline). Keep in sync.
interface DealCardServer {
  kind: 'deal';
  id: string;
  clientName: string;
  source: string;
  valueCents: number | null;
  ageDays: number;
  assignedBrokerName: string | null;
  linkedLoanId: string | null;
  rawStatus: string | null;
  unifiedStage: UnifiedStage;
  capturedAt: string | null;
  linkedLeadId: string | null;
  linkedEmailLower: string | null;
  derivedStage?: DerivedStage | null;
}

interface UnifiedPipelineResponse {
  stages: Record<UnifiedStage, DealCardServer[]>;
  totalDeals: number;
  unknownDeals: number;
  dealConversionPct: number | null;
  wonValueCents: number;
  windowDays: number;
}

// Firestore lead doc shape — matches services/client-functions writeLead
interface FirestoreLeadDoc {
  source?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  status?: string;
  assignedBrokerName?: string | null;
  linkedLoanId?: string | null;
  serviceCategory?: string | null;
  capturedAt?: Timestamp | null;
}

// Stage display metadata. Order is the column order across the page.
const STAGE_DISPLAY: Array<{
  stage: UnifiedStage;
  label: string;
  accent: string;
  description: string;
}> = [
  {
    stage: 'new',
    label: 'New',
    accent: 'border-t-blue-500',
    description: 'Just captured',
  },
  {
    stage: 'contacted',
    label: 'Contacted',
    accent: 'border-t-cyan-500',
    description: 'Broker reached out',
  },
  {
    stage: 'qualified',
    label: 'Qualified',
    accent: 'border-t-amber-500',
    description: 'Lodged / pre-approved',
  },
  {
    stage: 'deal_won',
    label: 'Deal Won',
    accent: 'border-t-emerald-500',
    description: 'Settled / closed',
  },
  {
    stage: 'deal_lost',
    label: 'Deal Lost',
    accent: 'border-t-red-500',
    description: 'Withdrawn / declined',
  },
];

// "Unknown" gets its own collapsed footer — not in the main column list.

// --- formatters ---
function fmtMoney(cents: number | null): string {
  if (cents === null) return '—';
  return formatCompactCurrency(cents);
}

function fmtAge(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.round(days / 30);
  return months === 1 ? '1 mo' : `${months} mos`;
}

// Source pill display palette — mirrors LeadsPage so cards feel
// consistent across the subapp. Falls back to slate-gray.
const SOURCE_BRAND: Record<string, { label: string; color: string }> = {
  facebook_lead_ads: { label: 'FB Lead Ad', color: '#1877f2' },
  meta_ads: { label: 'Meta', color: '#1877f2' },
  google_ads: { label: 'Google Ads', color: '#4285f4' },
  website_form: { label: 'Website', color: '#21759b' },
  referral: { label: 'Referral', color: '#8b5cf6' },
  manual: { label: 'Manual', color: '#6b7280' },
  csv_import: { label: 'CSV', color: '#6b7280' },
  mycrm: { label: 'MyCRM', color: '#22c55e' },
  nodify: { label: 'Nodify', color: '#06b6d4' },
  monday: { label: 'Monday', color: '#ec4899' },
  smokeball: { label: 'Smokeball', color: '#f59e0b' },
  other: { label: 'Other', color: '#6b7280' },
};

function sourceBrand(s: string): { label: string; color: string } {
  return SOURCE_BRAND[s] ?? { label: s, color: '#6b7280' };
}

// Stage rank for the lead⇆deal collapse: terminal stages beat in-flight.
const STAGE_RANK: Record<UnifiedStage, number> = {
  unknown: 0,
  new: 1,
  contacted: 2,
  qualified: 3,
  deal_lost: 4,
  deal_won: 5,
};

// --- card ---
const CardView: React.FC<{ card: UnifiedCard }> = ({ card }) => {
  const brand = sourceBrand(card.source);
  const kindLabel = (
    <span
      className={`uppercase tracking-wider ${
        card.kind === 'deal' ? 'text-emerald-400' : 'text-blue-400'
      }`}
    >
      {card.kind}
    </span>
  );
  return (
    <div className="rounded-md border border-gray-700 bg-gray-900/60 p-3 hover:border-gray-500 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-white truncate">
          {card.clientName}
        </div>
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0"
          style={{
            background: `${brand.color}20`,
            color: brand.color,
            border: `1px solid ${brand.color}50`,
          }}
        >
          {brand.label}
        </span>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px]">
        <span className="font-mono text-gray-300">
          {fmtMoney(card.valueCents)}
        </span>
        <span className="text-gray-500">{fmtAge(card.ageDays)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px]">
        <span className="text-gray-400 truncate">
          {card.assignedBrokerName ?? (
            <span className="text-amber-400">Unassigned</span>
          )}
        </span>
        {card.derivedStage && DERIVED_STAGE_LABEL[card.derivedStage] ? (
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span
              data-testid="derived-stage-badge"
              className="inline-flex items-center px-1.5 py-0.5 rounded-full font-semibold bg-violet-500/10 text-violet-300 border border-violet-500/30"
            >
              {DERIVED_STAGE_LABEL[card.derivedStage]}
            </span>
            {kindLabel}
          </span>
        ) : (
          kindLabel
        )}
      </div>
    </div>
  );
};

// --- main page ---

const LeadPipelinePage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const role = userData?.role ?? '';

  // Marketing (Lead-Capture operator) is granted access to the 4 Lead-Capture
  // config tabs alongside super_admin / administrator (marketing RBAC §1 GRANT).
  const isAdmin = role === 'super_admin' || role === 'administrator' || role === 'marketing';

  // Filter state
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [serviceCategoryFilter, setServiceCategoryFilter] =
    useState<string>('all');
  const [lookbackDays, setLookbackDays] = useState<LookbackDays>(90);
  const [showUnknown, setShowUnknown] = useState(false);

  // Lead-side stream from Firestore.
  const [leadDocs, setLeadDocs] = useState<
    Array<{ id: string; data: FirestoreLeadDoc }>
  >([]);
  const [leadLoading, setLeadLoading] = useState(true);
  const [leadError, setLeadError] = useState<string | null>(null);

  // Deal-side from BQ callable.
  const [dealResp, setDealResp] = useState<UnifiedPipelineResponse | null>(
    null
  );
  const [dealLoading, setDealLoading] = useState(true);
  const [dealError, setDealError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Firestore subscription. Most-recent first, capped at 500. super_admin,
  // administrator and marketing all read company-wide — the `leads` rule grants
  // marketing the same lead scope as super_admin (commissions are redacted in
  // the callable response, not here).
  useEffect(() => {
    if (!companyId || !isAdmin) {
      setLeadDocs([]);
      setLeadLoading(false);
      return;
    }
    const colRef = collection(db, `companies/${companyId}/leads`);
    const q = query(colRef, orderBy('capturedAt', 'desc'), limit(500));
    setLeadLoading(true);
    setLeadError(null);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs: Array<{ id: string; data: FirestoreLeadDoc }> = [];
        snap.forEach((doc) =>
          docs.push({ id: doc.id, data: doc.data() as FirestoreLeadDoc })
        );
        setLeadDocs(docs);
        setLeadLoading(false);
      },
      (err) => {
        setLeadError(err.message);
        setLeadLoading(false);
      }
    );
    return () => unsub();
  }, [companyId, isAdmin]);

  // Callable fetch.
  useEffect(() => {
    if (!companyId || !isAdmin) {
      setDealResp(null);
      setDealLoading(false);
      return;
    }
    const fn = httpsCallable<
      { companyId: string; lookbackDays: LookbackDays },
      UnifiedPipelineResponse
    >(functions, 'getUnifiedPipeline');
    setDealLoading(true);
    setDealError(null);
    fn({ companyId, lookbackDays })
      .then((r) => setDealResp(r.data))
      .catch((err) => {
        console.error('[LeadPipelinePage] getUnifiedPipeline failed', err);
        setDealError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setDealLoading(false));
  }, [companyId, isAdmin, lookbackDays, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // Build the merged column data. Apply filters here so both sides
  // respect them in lockstep.
  const merged = useMemo(() => {
    const byStage: Record<UnifiedStage, UnifiedCard[]> = {
      unknown: [],
      new: [],
      contacted: [],
      qualified: [],
      deal_won: [],
      deal_lost: [],
    };
    const collapsed = new Set<string>(); // bucket keys we already absorbed

    const now = Date.now();

    // 1) Index lead docs by collapse keys (linked_loan_id, lowercased
    //    email). When a deal joins to one of these, we drop the lead
    //    side and let the deal card represent the client.
    type LeadEntry = {
      card: UnifiedCard;
      bucketKeys: string[];
    };
    const leadEntries: LeadEntry[] = [];
    const leadByBucket = new Map<string, LeadEntry>();

    for (const { id, data } of leadDocs) {
      const captured = data.capturedAt?.toDate
        ? data.capturedAt.toDate().toISOString()
        : null;
      const ageDays = captured
        ? Math.max(0, Math.floor((now - new Date(captured).getTime()) / 86_400_000))
        : 0;
      const status = data.status ?? 'new';
      // Lead doc.status uses Valutten's native 'new|contacted|qualified|
      // converted|disqualified'. Map directly: converted→deal_won,
      // disqualified→deal_lost. Anything else flows through the unified
      // stage registry (which canonicalises 'new'/'contacted'/'qualified'
      // already).
      let stage: UnifiedStage;
      if (status === 'converted') stage = 'deal_won';
      else if (status === 'disqualified') stage = 'deal_lost';
      else stage = toUnifiedStage('mycrm', status);

      const fullName =
        [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
        data.email ||
        id;

      const card: UnifiedCard = {
        kind: 'lead',
        id,
        clientName: fullName,
        source: data.source ?? 'unknown',
        valueCents: null,
        ageDays,
        assignedBrokerName: data.assignedBrokerName ?? null,
        linkedLoanId: data.linkedLoanId ?? null,
        rawStatus: status,
        unifiedStage: stage,
        capturedAt: captured,
        serviceCategory: data.serviceCategory ?? null,
        emailLower: data.email ? data.email.toLowerCase() : null,
      };

      const bucketKeys: string[] = [];
      if (data.linkedLoanId) bucketKeys.push(`loan:${data.linkedLoanId}`);
      if (data.email) bucketKeys.push(`email:${data.email.toLowerCase()}`);
      // Growth 'propose': the server joins leads.crm_deal_id = deal_id.
      bucketKeys.push(`lead:${id}`);

      const entry: LeadEntry = { card, bucketKeys };
      leadEntries.push(entry);
      for (const k of bucketKeys) {
        // First lead per bucket wins for joining purposes — sibling leads
        // (multi-service dedup softening) still get rendered separately.
        if (!leadByBucket.has(k)) leadByBucket.set(k, entry);
      }
    }

    // 2) Walk deal cards. If a deal joins to a lead via bucket key,
    //    collapse: keep the more-progressed stage's representation.
    if (dealResp) {
      for (const stage of UNIFIED_STAGES) {
        const dealList = dealResp.stages[stage] ?? [];
        for (const d of dealList) {
          // Build candidate bucket keys — same rule as the lead side.
          const dealBuckets: string[] = [];
          if (d.linkedLoanId) dealBuckets.push(`loan:${d.linkedLoanId}`);
          if (d.linkedEmailLower)
            dealBuckets.push(`email:${d.linkedEmailLower}`);
          // Only the 'propose' response sets the derivedStage key, so a
          // non-propose linkedLeadId (HEAD's email join) never collapses
          // anything (W2-D25).
          if (d.linkedLeadId && d.derivedStage !== undefined)
            dealBuckets.push(`lead:${d.linkedLeadId}`);

          let matched: LeadEntry | undefined;
          let matchedKey: string | undefined;
          for (const k of dealBuckets) {
            const found = leadByBucket.get(k);
            if (found) {
              matched = found;
              matchedKey = k;
              break;
            }
          }

          const dealCard: UnifiedCard = {
            kind: 'deal',
            id: d.id,
            clientName: d.clientName,
            source: d.source,
            valueCents: d.valueCents,
            ageDays: d.ageDays,
            assignedBrokerName: d.assignedBrokerName,
            linkedLoanId: d.linkedLoanId,
            rawStatus: d.rawStatus,
            unifiedStage: d.unifiedStage,
            capturedAt: d.capturedAt,
            serviceCategory: matched?.card.serviceCategory ?? null,
            emailLower: d.linkedEmailLower ?? null,
            derivedStage: d.derivedStage,
          };

          if (matched) {
            // Mark the lead as absorbed. A `lead:` match absorbs only the
            // joined lead; its siblings on the same email or loan stay.
            if (matchedKey?.startsWith('lead:')) collapsed.add(matchedKey);
            else for (const k of matched.bucketKeys) collapsed.add(k);
            // Pick the more-progressed stage for display.
            // A winning lead card keeps the deal's derived stage badge.
            const winner =
              STAGE_RANK[d.unifiedStage] >= STAGE_RANK[matched.card.unifiedStage]
                ? dealCard
                : d.derivedStage
                  ? { ...matched.card, derivedStage: d.derivedStage }
                  : matched.card;
            byStage[winner.unifiedStage].push(winner);
          } else {
            byStage[d.unifiedStage].push(dealCard);
          }
        }
      }
    }

    // 3) Lead-only cards (no matched deal).
    for (const entry of leadEntries) {
      const wasAbsorbed = entry.bucketKeys.some((k) => collapsed.has(k));
      if (wasAbsorbed) continue;
      byStage[entry.card.unifiedStage].push(entry.card);
    }

    // 4) Apply UI filters across all columns.
    const sourceFiltered = (cards: UnifiedCard[]) =>
      cards.filter((c) => {
        if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
        if (
          serviceCategoryFilter !== 'all' &&
          (c.serviceCategory ?? 'unknown') !== serviceCategoryFilter
        )
          return false;
        return true;
      });

    const filtered: Record<UnifiedStage, UnifiedCard[]> = {
      unknown: sourceFiltered(byStage.unknown),
      new: sourceFiltered(byStage.new),
      contacted: sourceFiltered(byStage.contacted),
      qualified: sourceFiltered(byStage.qualified),
      deal_won: sourceFiltered(byStage.deal_won),
      deal_lost: sourceFiltered(byStage.deal_lost),
    };

    // 5) Sort within each column — newest first.
    for (const s of UNIFIED_STAGES) {
      filtered[s].sort((a, b) => a.ageDays - b.ageDays);
    }

    return filtered;
  }, [leadDocs, dealResp, sourceFilter, serviceCategoryFilter]);

  // Derived stats for the top tiles.
  const totals = useMemo(() => {
    let leads = 0;
    let deals = 0;
    let won = 0;
    let total = 0;
    let wonValueCents = 0;
    for (const s of UNIFIED_STAGES) {
      for (const c of merged[s]) {
        if (c.kind === 'lead') leads += 1;
        else deals += 1;
        if (c.unifiedStage === 'deal_won') {
          won += 1;
          if (c.valueCents != null) wonValueCents += c.valueCents;
        }
        total += 1;
      }
    }
    const conversionPct =
      total > 0 ? Math.round((won / total) * 1000) / 10 : null;
    return { leads, deals, won, total, conversionPct, wonValueCents };
  }, [merged]);

  // Distinct source / serviceCategory values for the filter dropdowns —
  // dynamically derived from observed data so the dropdowns never offer
  // a value that has no rows.
  const allSources = useMemo(() => {
    const set = new Set<string>();
    for (const s of UNIFIED_STAGES) {
      for (const c of merged[s]) set.add(c.source);
      // Also include lead docs that were filtered out by current source
      // — otherwise switching to a different source would leave the user
      // stuck on `all`.
    }
    for (const { data } of leadDocs) {
      if (data.source) set.add(data.source);
    }
    if (dealResp) {
      for (const s of UNIFIED_STAGES) {
        for (const d of dealResp.stages[s] ?? []) set.add(d.source);
      }
    }
    return Array.from(set).sort();
  }, [merged, leadDocs, dealResp]);

  const allServiceCategories = useMemo(() => {
    const set = new Set<string>();
    for (const { data } of leadDocs) {
      if (data.serviceCategory) set.add(data.serviceCategory);
    }
    return Array.from(set).sort();
  }, [leadDocs]);

  const loading = leadLoading || dealLoading;
  const error = leadError || dealError;

  if (!companyId) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Layers}
          title="Unified Pipeline"
          subtitle="All leads and CRM deals across services in one view."
        />
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200">
          Loading user context…
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Layers}
          title="Unified Pipeline"
          subtitle="All leads and CRM deals across services in one view."
        />
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-8 text-center max-w-lg">
            <h3 className="text-lg font-semibold text-red-300 mb-2">
              Access denied
            </h3>
            <p className="text-sm text-gray-300">
              The Unified Pipeline view is available to super-admins and
              administrators only. Brokers should use the Lead Inbox tab.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Layers}
        title="Unified Pipeline"
        subtitle="All leads and CRM deals across services in one view."
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          icon={Users}
          label="Total leads"
          value={totals.leads.toLocaleString()}
          accent="border-t-blue-500"
          hint="From Valutten lead capture"
        />
        <StatTile
          icon={Briefcase}
          label="Total deals"
          value={totals.deals.toLocaleString()}
          accent="border-t-emerald-500"
          hint="From connected CRMs"
        />
        <StatTile
          icon={TrendingUp}
          label="Conversion rate"
          value={
            totals.conversionPct === null
              ? '—'
              : `${totals.conversionPct.toFixed(1)}%`
          }
          accent="border-t-amber-500"
          hint={`${totals.won.toLocaleString()} won of ${totals.total.toLocaleString()} total`}
        />
        <StatTile
          icon={PiggyBank}
          label={`Won (${lookbackDays === 0 ? 'all' : `${lookbackDays}d`})`}
          value={fmtMoney(totals.wonValueCents)}
          accent="border-t-violet-500"
          hint="Settled deal volume"
        />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <Filter className="w-4 h-4 text-gray-500" />
        <FilterSelect
          label="Source"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: 'all', label: 'All sources' },
            ...allSources.map((s) => ({ value: s, label: sourceBrand(s).label })),
          ]}
        />
        <FilterSelect
          label="Service"
          value={serviceCategoryFilter}
          onChange={setServiceCategoryFilter}
          options={[
            { value: 'all', label: 'All services' },
            ...allServiceCategories.map((s) => ({ value: s, label: s })),
          ]}
          disabled={allServiceCategories.length === 0}
        />
        <FilterSelect
          label="Window"
          value={String(lookbackDays)}
          onChange={(v) => setLookbackDays(Number(v) as LookbackDays)}
          options={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: '90 days' },
            { value: '0', label: 'All time' },
          ]}
        />
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Loading…
          </span>
        )}
      </div>

      {/* Columns — 6 (5 visible + Unknown collapsed) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {STAGE_DISPLAY.map((sd) => {
          const cards = merged[sd.stage];
          return (
            <div
              key={sd.stage}
              className={`rounded-lg border border-gray-700 bg-gray-800/40 ${sd.accent} border-t-2 flex flex-col`}
            >
              <div className="px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white">
                    {sd.label}
                  </h3>
                  <span className="text-[11px] font-mono text-gray-400">
                    {cards.length}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {sd.description}
                </p>
              </div>
              <div className="p-3 space-y-2 min-h-[200px]">
                {cards.length === 0 ? (
                  <div className="text-xs text-gray-500 text-center py-6">
                    No items in this stage
                  </div>
                ) : (
                  cards
                    .slice(0, 50)
                    .map((c) => (
                      <CardView key={`${c.kind}:${c.id}`} card={c} />
                    ))
                )}
                {cards.length > 50 && (
                  <div className="text-[10px] text-gray-500 text-center pt-2">
                    +{cards.length - 50} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unknown column — collapsed by default */}
      {merged.unknown.length > 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40">
          <button
            onClick={() => setShowUnknown((v) => !v)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-800/60 transition-colors"
          >
            <div>
              <h3 className="text-sm font-semibold text-white">
                Unknown stage
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  +{merged.unknown.length} unmapped
                </span>
              </h3>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Raw CRM statuses that don&apos;t map to a unified stage yet —
                {' '}
                add overrides via the upcoming admin override UI.
              </p>
            </div>
            {showUnknown ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          {showUnknown && (
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {merged.unknown.slice(0, 60).map((c) => (
                <CardView key={`${c.kind}:${c.id}`} card={c} />
              ))}
              {merged.unknown.length > 60 && (
                <div className="col-span-full text-[10px] text-gray-500 text-center pt-2">
                  +{merged.unknown.length - 60} more
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Provider note — when there are deals from non-mycrm providers we
          remind the admin the unified stage map already covers them. */}
      {dealResp && dealResp.totalDeals > 0 && (
        <p className="text-[10px] text-gray-500 text-center">
          Showing the latest snapshot per deal. Stage mapping respects the
          per-CRM registry (currently: MyCRM). Add more CRMs from Settings
          → Integrations.
        </p>
      )}
    </div>
  );
};

// --- helper components ---

const StatTile: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  accent: string;
  hint?: string;
}> = ({ icon: Icon, label, value, accent, hint }) => (
  <div
    className={`rounded-lg border border-gray-700 bg-gray-900/40 ${accent} border-t-2 p-4`}
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className="mt-1 text-2xl font-bold text-white">{value}</div>
    {hint && <div className="mt-1 text-[10px] text-gray-500">{hint}</div>}
  </div>
);

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
}> = ({ label, value, onChange, options, disabled }) => (
  <label className="inline-flex items-center gap-2 text-xs text-gray-400">
    <span>{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-white text-xs focus:outline-none focus:border-blue-500 disabled:opacity-50"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

export default LeadPipelinePage;
